import { Form } from "./Form";
import {
	IFormConfig,
	IItemConfig,
	ItemEvent,
	FormEvents,
	ICheckboxGroupValue,
	IOption,
	ISimpleVaultValue,
	IContainerConfig,
	IColorPickerConfig,
	ITimePickerConfig,
	ISliderFormConfig,
	ISimpleVaultConfig,
	ISelectConfig,
	IRadioGroupConfig,
	IComboConfig,
	ICheckboxGroupConfig,
	IDatePickerConfig,
} from "./types";
import { ILayoutConfig, ProLayout, ICellConfig } from "@dhx/ts-layout";
import { uid } from "../../ts-common/core";
import { Button } from "./elements/button";
import { DatePicker } from "./elements/dateinput";
import { Checkbox } from "./elements/checkbox";
import { CheckboxGroup } from "./elements/checkboxGroup";
import { Input } from "./elements/input";
import { RadioGroup } from "./elements/radioGroup";
import { Select } from "./elements/select";
import { SimpleVault } from "./elements/simplevault";
import { SliderForm } from "./elements/sliderform";
import { Textarea } from "./elements/textarea";
import { Text } from "./elements/textinput";
import { TimePicker } from "./elements/timeinput";
import { ITimeObject } from "../../ts-timepicker";
import { ColorPicker } from "./elements/colorpicker";
import { Container } from "./elements/container";
import { Spacer } from "./elements/spacer";
import { ProCombo } from "./elements/proCombo";

export class ProForm extends Form {
	constructor(container: HTMLElement | string, config: IFormConfig) {
		super(container, config);
	}
	protected _addLayoutItem(item: IItemConfig) {
		const id = (item.id = item.id || uid());
		const name = (item.name = item.name || id.toString());
		item.type = item.type && (item.type.toLowerCase() as any);
		let { width, height } = item;
		const { css, padding, ...config } = item;
		let classList = css ? css + " dhx_form-element" : "dhx_form-element";
		const autoSize = !(item.type === "spacer" || item.type === undefined);
		if (autoSize && !width) {
			width = "content";
		}
		if (autoSize && !height) {
			height = "content";
		}

		switch (config.type) {
			case "button":
				{
					if (config.full) {
						classList += " dhx_button--full-gravity";
					}
					const button = (this._attachments[name] = new Button(null, item));
					button.events.on(ItemEvent.beforeChangeProperties, props => {
						return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
					});
					button.events.on(ItemEvent.afterChangeProperties, props => {
						this._changeProps(name, props);
						this.events.fire(FormEvents.afterChangeProperties, [name, props]);
						this.layout.paint();
					});
					button.events.on(ItemEvent.click, e => {
						e.preventDefault();
						this.events.fire(FormEvents.click, [name, e]);
						this.events.fire(FormEvents.buttonClick, [name, e]);

						if (button.config.submit && this.validate()) {
							button.config.url && this.send(button.config.url);
						}
					});
					button.events.on(ItemEvent.beforeHide, (text: string, init: boolean) => {
						if (!init) {
							return this.events.fire(FormEvents.beforeHide, [name, text]);
						}
					});
					button.events.on(ItemEvent.beforeShow, (text: string) => {
						return this.events.fire(FormEvents.beforeShow, [name, text]);
					});
					button.events.on(ItemEvent.afterHide, (text: string, init: boolean) => {
						this.layout.getCell(name).hide();
						!init && this.events.fire(FormEvents.afterHide, [name, text]);
					});
					button.events.on(ItemEvent.afterShow, (text: string) => {
						this.layout.getCell(name).show();
						this.events.fire(FormEvents.afterShow, [name, text]);
					});
					button.events.on(ItemEvent.focus, (text: string) => {
						this.events.fire(FormEvents.focus, [name, text]);
					});
					button.events.on(ItemEvent.blur, (text: string) => {
						this.events.fire(FormEvents.blur, [name, text]);
					});
					button.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
						this.events.fire(FormEvents.keydown, [event, name]);
					});
				}
				break;
			case "datepicker": {
				const datePicker = (this._attachments[name] = new DatePicker(
					null,
					item as IDatePickerConfig
				));
				this._state[name] = datePicker.getValue(config.valueFormat === "Date");

				datePicker.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				datePicker.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = datePicker.getValue(config.valueFormat === "Date");
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.layout.paint();
				});
				datePicker.events.on(ItemEvent.change, (value: string | Date) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				datePicker.events.on(ItemEvent.beforeHide, (value: string | Date, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				datePicker.events.on(ItemEvent.beforeShow, (value: string | Date) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				datePicker.events.on(ItemEvent.afterHide, (value: string | Date, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				datePicker.events.on(ItemEvent.afterShow, (value: string | Date) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				datePicker.events.on(ItemEvent.beforeValidate, (value: string | Date) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				datePicker.events.on(ItemEvent.afterValidate, (value: string | Date, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				datePicker.events.on(ItemEvent.focus, (value: string | Date) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				datePicker.events.on(ItemEvent.blur, (value: string | Date) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				datePicker.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "checkbox": {
				const checkbox = (this._attachments[name] = new Checkbox(null, item));
				this._state[name] = checkbox.getValue();

				checkbox.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				checkbox.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = checkbox.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.layout.paint();
				});
				checkbox.events.on(ItemEvent.change, (value: string | boolean) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				checkbox.events.on(ItemEvent.beforeHide, (value: string | boolean, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				checkbox.events.on(ItemEvent.beforeShow, (value: string | boolean) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				checkbox.events.on(ItemEvent.afterHide, (value: string | boolean, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				checkbox.events.on(ItemEvent.afterShow, (value: string | boolean) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				checkbox.events.on(ItemEvent.beforeValidate, (value: string | boolean) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				checkbox.events.on(ItemEvent.afterValidate, (value: string | boolean, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				checkbox.events.on(ItemEvent.focus, (value: string | boolean) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				checkbox.events.on(ItemEvent.blur, (value: string | boolean) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				checkbox.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "checkboxgroup": {
				const checkboxGroup = (this._attachments[name] = new CheckboxGroup(
					null,
					item as ICheckboxGroupConfig
				));
				this._state[name] = checkboxGroup.getValue();

				checkboxGroup.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				checkboxGroup.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = checkboxGroup.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.layout.paint();
				});
				checkboxGroup.events.on(ItemEvent.change, (value: ICheckboxGroupValue) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				checkboxGroup.events.on(ItemEvent.beforeHide, (value: ICheckboxGroupValue, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				checkboxGroup.events.on(ItemEvent.beforeShow, (value: ICheckboxGroupValue) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				checkboxGroup.events.on(ItemEvent.afterHide, (value: ICheckboxGroupValue, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				checkboxGroup.events.on(ItemEvent.afterShow, (value: ICheckboxGroupValue) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				checkboxGroup.events.on(ItemEvent.beforeValidate, (value: ICheckboxGroupValue) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				checkboxGroup.events.on(
					ItemEvent.afterValidate,
					(value: ICheckboxGroupValue, isValid: boolean) => {
						this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
					}
				);
				checkboxGroup.events.on(ItemEvent.focus, (value: ICheckboxGroupValue, id: string) => {
					this.events.fire(FormEvents.focus, [name, value, id]);
				});
				checkboxGroup.events.on(ItemEvent.blur, (value: ICheckboxGroupValue, id: string) => {
					this.events.fire(FormEvents.blur, [name, value, id]);
				});
				checkboxGroup.events.on(ItemEvent.keydown, (event: KeyboardEvent, id) => {
					this.events.fire(FormEvents.keydown, [event, name, id]);
				});
				break;
			}
			case "combo": {
				const combo = (this._attachments[name] = new ProCombo(null, item as IComboConfig));
				this._state[name] = combo.getValue();

				combo.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				combo.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = combo.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.layout.paint();
				});
				combo.events.on(ItemEvent.change, (value: string | string[]) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				combo.events.on(ItemEvent.beforeHide, (value: string | string[], init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				combo.events.on(ItemEvent.beforeShow, (value: string | string[]) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				combo.events.on(ItemEvent.afterHide, (value: string | string[], init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				combo.events.on(ItemEvent.afterShow, (value: string | string[]) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				combo.events.on(ItemEvent.beforeValidate, (value: string | string[]) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				combo.events.on(ItemEvent.afterValidate, (value: string | string[], isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				combo.events.on(ItemEvent.focus, (value: string | string[]) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				combo.events.on(ItemEvent.blur, (value: string | string[]) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				combo.events.on(ItemEvent.keydown, (event: KeyboardEvent, id: string | undefined) => {
					this.events.fire(FormEvents.keydown, [event, name, id]);
				});
				break;
			}
			case "input": {
				const input = (this._attachments[name] = new Input(null, item));
				this._state[name] = input.getValue();

				input.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				input.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = input.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.layout.paint();
				});
				input.events.on(ItemEvent.change, (value: string | number) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				input.events.on(ItemEvent.beforeHide, (value: string | number, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				input.events.on(ItemEvent.beforeShow, (value: string | number) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				input.events.on(ItemEvent.afterHide, (value: string | number, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				input.events.on(ItemEvent.afterShow, (value: string | number) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				input.events.on(ItemEvent.beforeValidate, (value: string | number) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				input.events.on(ItemEvent.afterValidate, (value: string | number, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				input.events.on(ItemEvent.focus, (value: string | number) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				input.events.on(ItemEvent.blur, (value: string | number) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				input.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "radiogroup": {
				const radioGroup = (this._attachments[name] = new RadioGroup(
					null,
					item as IRadioGroupConfig
				));
				this._state[name] = radioGroup.getValue();

				radioGroup.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				radioGroup.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = radioGroup.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.layout.paint();
				});
				radioGroup.events.on(ItemEvent.change, (value: string) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				radioGroup.events.on(ItemEvent.beforeHide, (value: string, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				radioGroup.events.on(ItemEvent.beforeShow, (value: string) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				radioGroup.events.on(ItemEvent.afterHide, (value: string, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				radioGroup.events.on(ItemEvent.afterShow, (value: string) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				radioGroup.events.on(ItemEvent.beforeValidate, (value: string) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				radioGroup.events.on(ItemEvent.afterValidate, (value: string, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				radioGroup.events.on(ItemEvent.focus, (value: string, id: string) => {
					this.events.fire(FormEvents.focus, [name, value, id]);
				});
				radioGroup.events.on(ItemEvent.blur, (value: string, id: string) => {
					this.events.fire(FormEvents.blur, [name, value, id]);
				});
				radioGroup.events.on(ItemEvent.keydown, (event: KeyboardEvent, id: string) => {
					this.events.fire(FormEvents.keydown, [event, name, id]);
				});
				break;
			}
			case "select": {
				const select = (this._attachments[name] = new Select(null, item as ISelectConfig));
				this._state[name] = select.getValue();

				select.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				select.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = select.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.layout.paint();
				});
				select.events.on(ItemEvent.changeOptions, (options: IOption[]) => {
					this.layout.getCell(name).config.options = [...options];
				});
				select.events.on(ItemEvent.change, (value: string | number) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				select.events.on(ItemEvent.beforeHide, (value: string | number, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				select.events.on(ItemEvent.beforeShow, (value: string | number) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				select.events.on(ItemEvent.afterHide, (value: string | number, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				select.events.on(ItemEvent.afterShow, (value: string | number) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				select.events.on(ItemEvent.beforeValidate, (value: string | number) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				select.events.on(ItemEvent.afterValidate, (value: string | number, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				select.events.on(ItemEvent.focus, (value: string | number) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				select.events.on(ItemEvent.blur, (value: string | number) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				select.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "simplevault": {
				config.$vaultHeight = height;
				const simpleVault = (this._attachments[name] = new SimpleVault(
					null,
					item as ISimpleVaultConfig
				));
				this._state[name] = simpleVault.getValue();

				simpleVault.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				simpleVault.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = simpleVault.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.layout.paint();
				});
				simpleVault.events.on(ItemEvent.change, (value: ISimpleVaultValue[]) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				simpleVault.events.on(ItemEvent.beforeHide, (value: ISimpleVaultValue[], init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				simpleVault.events.on(ItemEvent.beforeShow, (value: ISimpleVaultValue[]) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				simpleVault.events.on(ItemEvent.afterHide, (value: ISimpleVaultValue[], init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				simpleVault.events.on(ItemEvent.afterShow, (value: ISimpleVaultValue[]) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				simpleVault.events.on(ItemEvent.beforeValidate, (value: ISimpleVaultValue[]) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				simpleVault.events.on(
					ItemEvent.afterValidate,
					(value: ISimpleVaultValue[], isValid: boolean) => {
						this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
					}
				);
				break;
			}
			case "slider": {
				const sliderForm = (this._attachments[name] = new SliderForm(
					null,
					item as ISliderFormConfig
				));
				this._state[name] = sliderForm.getValue();

				sliderForm.events.on(ItemEvent.change, (value: number[]) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				sliderForm.events.on(ItemEvent.beforeHide, (value: number[], init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				sliderForm.events.on(ItemEvent.beforeShow, (value: number[]) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				sliderForm.events.on(ItemEvent.afterHide, (value: number[], init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				sliderForm.events.on(ItemEvent.afterShow, (value: number[]) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				sliderForm.events.on(ItemEvent.focus, (value: number[]) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				sliderForm.events.on(ItemEvent.blur, (value: number[]) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				sliderForm.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "textarea": {
				const textarea = (this._attachments[name] = new Textarea(null, item));
				this._state[name] = textarea.getValue();

				textarea.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				textarea.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = textarea.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.layout.paint();
				});
				textarea.events.on(ItemEvent.change, (value: string | number) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				textarea.events.on(ItemEvent.beforeHide, (value: string | number, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				textarea.events.on(ItemEvent.beforeShow, (value: string | number) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				textarea.events.on(ItemEvent.afterHide, (value: string | number, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				textarea.events.on(ItemEvent.afterShow, (value: string | number) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				textarea.events.on(ItemEvent.beforeValidate, (value: string | number) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				textarea.events.on(ItemEvent.afterValidate, (value: string | number, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				textarea.events.on(ItemEvent.focus, (value: string) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				textarea.events.on(ItemEvent.blur, (value: string) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				textarea.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "text": {
				if (width === "content") {
					classList += " dhx_form-element--fit-content";
				}
				const text = (this._attachments[name] = new Text(null, item));
				this._state[name] = text.getValue();

				text.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				text.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = text.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.layout.paint();
				});
				text.events.on(ItemEvent.change, (value: string | number) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				text.events.on(ItemEvent.beforeHide, (value: string | number, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				text.events.on(ItemEvent.beforeShow, (value: string | number) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				text.events.on(ItemEvent.afterHide, (value: string | number, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				text.events.on(ItemEvent.afterShow, (value: string | number) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				text.events.on(ItemEvent.beforeValidate, (value: string | number) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				text.events.on(ItemEvent.afterValidate, (value: string | number, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				break;
			}
			case "timepicker": {
				const timePicker = (this._attachments[name] = new TimePicker(
					null,
					item as ITimePickerConfig
				));
				this._state[name] =
					(config.value && timePicker.getValue(config.valueFormat === "timeObject")) || "";

				timePicker.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				timePicker.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = timePicker.getValue(config.valueFormat === "timeObject");
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.layout.paint();
				});
				timePicker.events.on(ItemEvent.change, (value: string | ITimeObject) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				timePicker.events.on(ItemEvent.beforeHide, (value: string | ITimeObject, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				timePicker.events.on(ItemEvent.beforeShow, (value: string | ITimeObject) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				timePicker.events.on(ItemEvent.afterHide, (value: string | ITimeObject, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				timePicker.events.on(ItemEvent.afterShow, (value: string | ITimeObject) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				timePicker.events.on(ItemEvent.beforeValidate, (value: string | ITimeObject) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				timePicker.events.on(
					ItemEvent.afterValidate,
					(value: string | ITimeObject, isValid: boolean) => {
						this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
					}
				);
				timePicker.events.on(ItemEvent.focus, (value: string | ITimeObject) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				timePicker.events.on(ItemEvent.blur, (value: string | ITimeObject) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				timePicker.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "colorpicker": {
				const colorPicker = (this._attachments[name] = new ColorPicker(
					null,
					item as IColorPickerConfig
				));
				this._state[name] = colorPicker.getValue();

				colorPicker.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				colorPicker.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this._state[name] = colorPicker.getValue();
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.layout.paint();
				});
				colorPicker.events.on(ItemEvent.change, (value: string) => {
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				colorPicker.events.on(ItemEvent.beforeHide, (value: string, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				colorPicker.events.on(ItemEvent.beforeShow, (value: string) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				colorPicker.events.on(ItemEvent.afterHide, (value: string, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				colorPicker.events.on(ItemEvent.afterShow, (value: string) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				colorPicker.events.on(ItemEvent.beforeValidate, (value: string) => {
					return this.events.fire(FormEvents.beforeValidate, [name, value]);
				});
				colorPicker.events.on(ItemEvent.afterValidate, (value: string, isValid: boolean) => {
					this.events.fire(FormEvents.afterValidate, [name, value, isValid]);
				});
				colorPicker.events.on(ItemEvent.focus, (value: string) => {
					this.events.fire(FormEvents.focus, [name, value]);
				});
				colorPicker.events.on(ItemEvent.blur, (value: string) => {
					this.events.fire(FormEvents.blur, [name, value]);
				});
				colorPicker.events.on(ItemEvent.keydown, (event: KeyboardEvent) => {
					this.events.fire(FormEvents.keydown, [event, name]);
				});
				break;
			}
			case "container": {
				const container = (this._attachments[name] = new Container(null, item as IContainerConfig));

				container.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				container.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.layout.paint();
				});
				container.events.on(ItemEvent.beforeHide, (value: string, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				container.events.on(ItemEvent.beforeShow, (value: string) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				container.events.on(ItemEvent.afterHide, (value: string, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				container.events.on(ItemEvent.afterShow, (value: string) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				break;
			}
			case "spacer":
			default: {
				const spacer = (this._attachments[name] = new Spacer(null, item));

				spacer.events.on(ItemEvent.beforeChangeProperties, props => {
					return this.events.fire(FormEvents.beforeChangeProperties, [name, props]);
				});
				spacer.events.on(ItemEvent.afterChangeProperties, props => {
					this._changeProps(name, props);
					this.events.fire(FormEvents.afterChangeProperties, [name, props]);
					this.layout.paint();
				});
				spacer.events.on(ItemEvent.beforeHide, (value: string, init: boolean) => {
					if (!init) {
						return this.events.fire(FormEvents.beforeHide, [name, value]);
					}
				});
				spacer.events.on(ItemEvent.beforeShow, (value: string) => {
					return this.events.fire(FormEvents.beforeShow, [name, value]);
				});
				spacer.events.on(ItemEvent.afterHide, (value: string, init: boolean) => {
					this.layout.getCell(name).hide();
					!init && this.events.fire(FormEvents.afterHide, [name, value]);
				});
				spacer.events.on(ItemEvent.afterShow, (value: string) => {
					this.layout.getCell(name).show();
					this.events.fire(FormEvents.afterShow, [name, value]);
				});
				break;
			}
		}

		const cell: ICellConfig = {
			id: name,
			width,
			height,
			padding,
			css: classList,
		};

		if ("gravity" in item) {
			cell.gravity = item.gravity;
		}

		return cell;
	}
	protected _initUI(container: HTMLElement | string) {
		const attachments = (this._attachments = {});
		const layoutConfig: ILayoutConfig = {
			padding: "8px",
		};
		this.config.css += " dhx_form";

		this._createLayoutConfig(this.config, layoutConfig);

		const layout = (this.layout = new ProLayout(container, layoutConfig));

		for (const name in attachments) {
			layout.getCell(name).attach(attachments[name]);
		}
	}
}
